/*
 * EntityMutate3.tsx
 *
 * Eins der Standard-Datenbankobjekte (Kunde etc) in einem Panel neu anlegen oder bearbeiten
 * basiert auf https://github.com/rjsf-team/react-jsonschema-form
 *
 * Üblicherweise benutzt man das nicht direkt
 * sondern useAddPanel() oder useEditPanel().
 *
 * Created by Dr. Maximillian Dornseif 2020-10-15
 * Copyright 2020, 2021, 2023 Dr. Maximillian Dornseif
 *
 * Created by Dr. Maximillian Dornseif 2023-10-06 in huWaWi3 33.1.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import { merge } from '@fluentui/react';
import { useSchema , LadeChecker } from '@hudora/hd-react-components';
import { AlertTitle } from '@mui/material';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { assertIsDefined, assertIsObject, assertIsString } from 'assertate-debug';
import { DocumentNode } from 'graphql';
import { OMIT_PROPERTIES, cleanGqlInput, omitDeep } from 'graphql-clean-diff';
import { JSONSchema7 } from 'json-schema';
import React, { useCallback, useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer';
import ReactJson from 'react-json-view';
import { useMutation } from 'urql';

import { IEntity } from '../../../types';
import { DisplayUserInfo } from '../widgets/DisplayUserInfo';
import { HdSpinner } from '../widgets/HdSpinner';
import { EntityMutateForm3 } from './EntityMutateForm3';
import { getErrorMessageBar } from '../UiChecker'

// Entity in einem Panel Anlegen/Bearbeiten
export interface IEntityMutate3Props {
  entity: Partial<IEntity>; // Initiale Daten
  isOpen: boolean;
  schemaName: string;
  mutation: DocumentNode;
  title?: string; // Überschrift
  onSaved?: (message: React.ReactElement, newData: IEntity) => void; // wird mit einer Erfolgs- oder Fehlermeldung aufgerufen
}

export interface IPatchResponse {
  errors: string[];
  success: Boolean;
  oldRev: string;
  newRev: string;
  old: IEntity;
  new: IEntity;
  duration: number;
}

/** Vorhandene Entity ändern
 * */

export const EntityMutate3 = (props: IEntityMutate3Props) => {
  // Ruft das Schema ab. Überlicherweise ist das schon im Cache
  const { loading, error, schema } = useSchema(props.schemaName);

  if (loading || error || !schema) {
    return <LadeChecker loading={loading} error={error} label={`Lade Schema ${props.schemaName} …`} />;
  }
  assertIsObject(props.entity, 'props.entity');
  assertIsString(props.entity._rev, 'props.entity._rev');
  assertIsObject(schema, 'schema');
  return <_EntityMutateMitSchema3 {...props} schema={schema} />;
};

 const _EntityMutateMitSchema3 = (
  props: IEntityMutate3Props & {
    schema: JSONSchema7;
  }
) => {
  const [validationMessageBar, setValidationMessageBar] = useState(null);
  const [successMessage, setSuccessMessage] = useState<React.ReactElement>();
  const entity = { ...props.entity, _keyStr: undefined };
  const oldRev = entity._rev;
  assertIsString(oldRev, 'oldRev');
  // GraphQL Mutation definieren
  const [state, mutateEntity] = useMutation(props.mutation);
  const { fetching: saving } = state;
  // Wird aufgerufen, wenn das Panel geschlossen wird
  const saveCallback = useCallback((ne) => {
    uebertragen(ne);
  }, []);

  // Daten per GraphQL Mutation an den Server senden.
  const uebertragen = async (newEntity) => {
    setValidationMessageBar(null);

    // Das Alte System arbeitete hier mit einer "updateMutation", zB
    // hwPreisUpdate(designator: ID!, input: HwPreisInput!): HwPreis!

    // Heute bevorzugen wir eine "patchMutation", zB
    // hwPreisPatch(designator: ID!, input: HwPreisPatchInput!, oldRev: String!): HwPreis!

    // Dabei muss `oldRev` zwingenden den Wert haben, den `_rev` beim Lesen des Objektes hatte.

    const fInput = prepareForGqlPatch(entity, newEntity, props.schema);
    const variables = {
      input: fInput,
      designator: entity.designator,
      oldRev,
    };
    const result = await mutateEntity(variables);
    const errorMessageBar = checkErrorMessageBar3(result, newEntity, entity);
    if (errorMessageBar) {
      setValidationMessageBar(errorMessageBar);
    } else {
      setSuccessMessage(getSuccessMessage3(result?.data?.patchInfo));
    }
  };

  return (
    <div>
      {validationMessageBar}
      {!successMessage ? (
        <div>
          {saving ? <HdSpinner>Speichere…</HdSpinner> : null}
          <div style={saving ? { display: 'none' } : {}}>
            <EntityMutateForm3
              schema={props.schema}
              entity={entity}
              onSave={(params) => saveCallback(params)}
            >
              {validationMessageBar}
            </EntityMutateForm3>
          </div>
        </div>
      ) : (
        successMessage
      )}
    </div>
  );
};

/** Auf das Vorhandensein von Fehlern im GraphQL Layer checken */
function checkErrorMessageBar3(result, entityDiff, entity) {
  let errorMessageBar = null;
  // GraphQL Errors
  if (result?.errors?.map) {
    errorMessageBar = getErrorMessageBar(result);
  } else if (result?.errors) {
    errorMessageBar = getErrorMessageBar(result.errors);
  } else if (result?.error) {
    errorMessageBar = getErrorMessageBar(result.error);
  } else if (result === undefined) {
    errorMessageBar = getErrorMessageBar(
      'Schwieriger Server Fehler - vermutlich ist das Ding nicht erreichbar.'
    );
  }

  if (errorMessageBar === null) {
    assertIsDefined(result?.data?.patchInfo);
  }
  // MVCC Errors
  if (result?.data?.patchInfo?.success === false) {
    const oldE = cleanGqlInput(result?.data?.patchInfo?.old);
    const newE = cleanGqlInput({ ...entity, ...entityDiff });

    errorMessageBar = (
      <Alert severity="warning">
        <AlertTitle>Es ist ein Konflikt beim Server-Zugriff auftgetreten</AlertTitle>
        <div>Jemand anders hat die Daten verändert, während Sie daran gearbeitet haben.</div>
        <div>
          Die paralellen Änderungen erfolgten wohl durch{' '}
          <DisplayUserInfo props={result.data?.patchInfo?.audit?.[0]} />
          um {result.data.patchInfo.audit[0].created_at}.
        </div>{' '}
        <div>
          <ReactDiffViewer
            leftTitle="Server"
            oldValue={JSON.stringify(cleanGqlInput(oldE), null, 2)}
            rightTitle="Ihre Daten"
            newValue={JSON.stringify(cleanGqlInput(newE), null, 2)}
            splitView
            hideLineNumbers
            extraLinesSurroundingDiff={1}
            useDarkTheme={false}
            disableWordDiff
          />
        </div>
        <div>
          <em>Ihre</em> Änderungen wurden <b>nicht</b> gespeichert. Am besten laden sie die Seite neu und
          versuchen es erneut.
        </div>
      </Alert>
    );
  }

  return errorMessageBar;
}

/** Die Formulardaten anhand der Schemadaten "aufräumen".
 */
export function prepareForGqlPatch(oldEntity, formData: Record<string, any>, schema: JSONSchema7) {
  const newFormData = merge({}, oldEntity, formData);

  try {
    // Properties, die nicht im Schema sind, schreiben wir nicht zurück
    const schemaProps = Object.keys(schema.properties);
    for (const key of Object.keys(newFormData)) {
      // hier fehlt die Rekursion ...
      if (!schemaProps.includes(key)) {
        delete newFormData[key];
        // console.log('delete', key)
      }
      if (['_rev', '_keyStr', 'id'].includes(key)) {
        delete newFormData[key];
        // console.log('delete', key)
      }
      if (key.startsWith('_')) {
        delete newFormData[key];
        // console.log('delete', key)
      }
      if (newFormData[key] === oldEntity[key]) {
        delete newFormData[key];
        // console.log('delete', key)
      }
    }
  } catch (e) {
    console.error(e);
  }
  // Ein bisschen Rekursion machen wir schon.
  return omitDeep(omitDeep(newFormData, OMIT_PROPERTIES), ['designator', '_rev']);
}

/** Message Bar mit GraphQL-Success
 * */
 function getSuccessMessage3(patchResponse: IPatchResponse): React.ReactElement {
  return (
    <Stack sx={{ width: '100%' }} spacing={2}>
      <Alert severity="success">Der Datensatz wurde erfolgreich gespeichert:</Alert>

      <ReactDiffViewer
        leftTitle="alt"
        oldValue={JSON.stringify(cleanGqlInput(patchResponse?.old), null, 2)}
        rightTitle="neu"
        newValue={JSON.stringify(cleanGqlInput(patchResponse?.new), null, 2)}
        splitView
        hideLineNumbers
        extraLinesSurroundingDiff={1}
        useDarkTheme={false}
        disableWordDiff
      />

      <ReactJson name="Details" collapsed={true} displayObjectSize={false} src={patchResponse} />
    </Stack>
  );
}
